import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { ProjectId, User } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { getProjectMembersQueryKey } from 'queries';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { useAddProjectUsersLiteMutation } from 'queries/project';

interface ReturnProps {
  inviteMembersToProject: (
    projectId: number,
    selectedUsers: User[],
  ) => Promise<void>;
  actionPending: boolean;
}

export const useAddUsersToProject = (): ReturnProps => {
  const { mutateAsync: addProjectUsersLite, isLoading: actionPending } =
    useAddProjectUsersLiteMutation();
  const queryClient = useQueryClient();
  const assignUserTexts = {
    success: {
      title: useTranslation('invitation.success.title'),
      message: useTranslation('invitation.success.content'),
    },
    error: {
      title: useTranslation('invitation.team_member.error.title'),
      message: useTranslation('invitation.team_member.error.content'),
    },
  };

  const inviteMembersToProject = useCallback(
    (projectId: number, selectedUsers): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        if (projectId == null) {
          resolve();
        } else {
          addProjectUsersLite(
            {
              projectId,
              projectUsers: selectedUsers.map((user) => ({
                userId: user.userId,
              })),
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
                queryClient.invalidateQueries(
                  getProjectMembersQueryKey(ProjectId(projectId)),
                );
                toastService({
                  toastContainer: {},
                  title: assignUserTexts.success.title,
                  message: assignUserTexts.success.message,
                }).success();
                resolve();
              },
              onError: () => {
                toastService({
                  toastContainer: {},
                  title: assignUserTexts.error.title,
                  message: assignUserTexts.error.message,
                }).error();
                reject();
              },
            },
          );
        }
      });
    },
    [
      addProjectUsersLite,
      assignUserTexts.error.message,
      assignUserTexts.error.title,
      assignUserTexts.success.message,
      assignUserTexts.success.title,
      queryClient,
    ],
  );

  return {
    inviteMembersToProject,
    actionPending,
  };
};
